import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Download,
  Building2,
  User as UserIcon,
  ShieldAlert,
  X,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuthStore } from "../store/authStore";
import { api } from "../lib/api";
import { contactsApi } from "../lib/contactsApi";
import AddContactDialog from "../components/contacts/AddContactDialog";
import ContactDetailDrawer from "../components/contacts/ContactDetailDrawer";
import ContactStatusChip from "../components/contacts/ContactStatusChip";

/**
 * Shared list page for `kind ∈ {person, company}`. Rendered by two thin
 * wrappers (`ContactsPeople`, `ContactsCompanies`) so the sidebar can link
 * directly and each page keeps its own URL.
 */
export default function ContactsListPage({ kind }) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [contacts, setContacts] = useState([]);
  const [otherKindContacts, setOtherKindContacts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // Nested "quick-add opposite kind" dialog — owned here so we don't render
  // AddContactDialog inside itself (breaks Babel's traversal).
  const [nestedOpen, setNestedOpen] = useState(false);
  const [nestedKind, setNestedKind] = useState(null);
  // Latest nested-created doc. AddContactDialog's `nestedCreated` prop watches
  // this via useEffect to update its local form / auth list.
  const [nestedCreated, setNestedCreated] = useState(null);

  const canManage =
    ["super_admin", "ultra_admin"].includes(user?.role) || user?.can_manage_contacts !== false;

  // Convenience projections. `people` and `companies` are always up-to-date
  // regardless of the current page kind — needed by the AddContactDialog so
  // its nested "quick-add opposite kind" flow can preselect + link correctly.
  const people = kind === "person" ? contacts : otherKindContacts;
  const companies = kind === "company" ? contacts : otherKindContacts;

  const load = async () => {
    try {
      const [rows, other, st, us, tms] = await Promise.all([
        contactsApi.list({ kind }),
        contactsApi.list({ kind: kind === "person" ? "company" : "person" }),
        contactsApi.listStatuses(kind),
        // Reverse-lookup data for the company detail drawer's
        // "Bu şirketten sorumlu" section. Both are cheap and small.
        api.get("/users").then((r) => r.data).catch(() => []),
        api.get("/teams").then((r) => r.data).catch(() => []),
      ]);
      setContacts(rows);
      setOtherKindContacts(other);
      setStatuses(st);
      setUsers(us || []);
      setTeams(tms || []);
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const filtered = useMemo(() => {
    let list = contacts;
    if (statusFilter !== "__all__") {
      list = list.filter((c) => c.status_id === statusFilter);
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((c) => {
        const hay = [
          c.first_name,
          c.last_name,
          c.name,
          c.email,
          c.phone,
          c.industry,
          ...(c.tags || []),
          c.notes,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(needle);
      });
    }
    return list;
  }, [contacts, statusFilter, q]);

  const downloadCsv = async () => {
    try {
      const res = await api.get(`/contacts/export.csv?kind=${kind}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contacts-${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // A brand-new contact may match the current page's kind (goes into
  // `contacts`) or the opposite kind — happens when the user uses the
  // nested quick-add ("+ Yeni kişi" inside company dialog and vice-versa).
  const onCreated = (created) => {
    if (created.kind === kind) {
      setContacts((cur) => [created, ...cur]);
    } else {
      setOtherKindContacts((cur) => [created, ...cur]);
    }
  };
  const onChanged = (updated, meta) => {
    if (meta?.deleted) {
      setContacts((cur) => cur.filter((c) => c.id !== meta.deleted));
      setOtherKindContacts((cur) => cur.filter((c) => c.id !== meta.deleted));
      setSelected(null);
      return;
    }
    if (updated) {
      if (updated.kind === kind) {
        setContacts((cur) => cur.map((c) => (c.id === updated.id ? updated : c)));
      } else {
        setOtherKindContacts((cur) =>
          cur.map((c) => (c.id === updated.id ? updated : c)),
        );
      }
      setSelected(updated);
    }
  };

  return (
    <div className="space-y-6" data-testid={`contacts-page-${kind}`}>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
          {t("brand")}
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {kind === "person" ? t("contacts.title_people") : t("contacts.title_companies")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          {kind === "person"
            ? t("contacts.subtitle_people")
            : t("contacts.subtitle_companies")}
        </p>
      </div>

      {!canManage && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <ShieldAlert className="h-4 w-4 flex-none" />
          <span>{t("contacts.no_permission")}</span>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("contacts.search_placeholder")}
              className="pl-9 pr-9"
              data-testid="contacts-search"
            />
            {q && (
              /* Reset filter — sadece input dolu iken görünür. */
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Aramayı temizle"
                title="Aramayı temizle"
                data-testid="contacts-search-clear"
                className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-48"
              data-testid="contacts-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">
                {t("contacts.filter_status")}: {t("contacts.filter_all")}
              </SelectItem>
              {statuses
                .filter((s) => s.active !== false)
                .map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={downloadCsv}
            className="gap-1 border-zinc-300"
            data-testid="contacts-export-csv"
          >
            <Download className="h-3.5 w-3.5" />
            {t("contacts.export_csv")}
          </Button>
          {canManage && (
            <Button
              size="sm"
              onClick={() => setAddOpen(true)}
              data-testid="contacts-add-btn"
              className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
            >
              <Plus className="h-3.5 w-3.5" />
              {kind === "person" ? t("contacts.add_person") : t("contacts.add_company")}
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
        {filtered.length === 0 ? (
          <div className="px-6 py-14 text-center text-sm text-zinc-500">
            {kind === "person"
              ? t("contacts.empty_people")
              : t("contacts.empty_companies")}
          </div>
        ) : (
          <>
            {/* ── Desktop table ── */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
              <thead className="border-b border-zinc-100 bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">
                    {t("contacts.col_name")}
                  </th>
                  {kind === "person" && (
                    <th className="px-4 py-3 text-left font-semibold">
                      {t("contacts.col_company")}
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-semibold">
                    {t("contacts.col_email")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">
                    {t("contacts.col_phone")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">
                    {t("contacts.col_status")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">
                    {t("contacts.col_tags")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.map((c) => {
                  const status = statuses.find((s) => s.id === c.status_id);
                  const co = companies.find((x) => x.id === c.company_id);
                  const displayName =
                    c.kind === "person"
                      ? [c.first_name, c.last_name].filter(Boolean).join(" ") || "—"
                      : c.name || "—";
                  return (
                    <tr
                      key={c.id}
                      onClick={() => setSelected(c)}
                      data-testid={`contact-row-${c.id}`}
                      className="cursor-pointer transition-colors hover:bg-zinc-50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className={`flex h-8 w-8 items-center justify-center rounded-full text-white ${
                              c.kind === "person"
                                ? "bg-gradient-to-br from-violet-500 to-fuchsia-500"
                                : "bg-gradient-to-br from-sky-500 to-cyan-500"
                            }`}
                          >
                            {c.kind === "person" ? (
                              <UserIcon className="h-4 w-4" />
                            ) : (
                              <Building2 className="h-4 w-4" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate font-medium text-zinc-900">
                              {displayName}
                            </div>
                            {c.kind === "person" && c.title && (
                              <div className="truncate text-[11px] text-zinc-500">
                                {c.title}
                              </div>
                            )}
                            {c.kind === "company" && c.industry && (
                              <div className="truncate text-[11px] text-zinc-500">
                                {c.industry}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      {kind === "person" && (
                        <td className="px-4 py-3 text-zinc-600">
                          {co?.name || "—"}
                        </td>
                      )}
                      <td className="px-4 py-3 text-zinc-600">{c.email || "—"}</td>
                      <td className="px-4 py-3 text-zinc-600">{c.phone || "—"}</td>
                      <td className="px-4 py-3">
                        <ContactStatusChip status={status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(c.tags || []).slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-700"
                            >
                              {tag}
                            </span>
                          ))}
                          {(c.tags || []).length > 3 && (
                            <span className="text-[11px] text-zinc-400">
                              +{c.tags.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {/* ── Mobile card list ── */}
            <ul className="divide-y divide-zinc-100 md:hidden">
              {filtered.map((c) => {
                const status = statuses.find((s) => s.id === c.status_id);
                const co = companies.find((x) => x.id === c.company_id);
                const displayName =
                  c.kind === "person"
                    ? [c.first_name, c.last_name].filter(Boolean).join(" ") ||
                      "—"
                    : c.name || "—";
                return (
                  <li
                    key={c.id}
                    onClick={() => setSelected(c)}
                    data-testid={`contact-row-${c.id}`}
                    className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors active:bg-zinc-50"
                  >
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white ${
                        c.kind === "person"
                          ? "bg-gradient-to-br from-violet-500 to-fuchsia-500"
                          : "bg-gradient-to-br from-sky-500 to-cyan-500"
                      }`}
                    >
                      {c.kind === "person" ? (
                        <UserIcon className="h-4 w-4" />
                      ) : (
                        <Building2 className="h-4 w-4" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 truncate text-sm font-semibold text-zinc-900">
                          {displayName}
                        </div>
                        <ContactStatusChip status={status} />
                      </div>
                      {c.kind === "person" && (c.title || co?.name) && (
                        <div className="truncate text-[11px] text-zinc-500">
                          {[c.title, co?.name].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      {c.kind === "company" && c.industry && (
                        <div className="truncate text-[11px] text-zinc-500">
                          {c.industry}
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-zinc-600">
                        {c.email && (
                          <span className="truncate">{c.email}</span>
                        )}
                        {c.email && c.phone && (
                          <span className="text-zinc-300">·</span>
                        )}
                        {c.phone && <span>{c.phone}</span>}
                      </div>
                      {(c.tags || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(c.tags || []).slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-700"
                            >
                              {tag}
                            </span>
                          ))}
                          {(c.tags || []).length > 3 && (
                            <span className="text-[10px] text-zinc-400">
                              +{c.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      <AddContactDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        initialKind={kind}
        companies={companies}
        people={people}
        onCreated={onCreated}
        onRequestAddOpposite={(k) => {
          setNestedKind(k);
          setNestedOpen(true);
        }}
        nestedCreated={nestedCreated}
      />

      {/* Nested add-opposite-kind dialog. Its onCreated bubbles through both
          the main list refresh AND setNestedCreated so the outer form picks
          the new record up. */}
      <AddContactDialog
        open={nestedOpen}
        onOpenChange={(v) => {
          setNestedOpen(v);
          if (!v) setNestedKind(null);
        }}
        initialKind={nestedKind}
        companies={companies}
        people={people}
        isNested
        onCreated={(created) => {
          onCreated(created);
          setNestedCreated(created);
        }}
      />

      <ContactDetailDrawer
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        contact={selected}
        statuses={statuses}
        companies={companies}
        people={people}
        users={users}
        teams={teams}
        onChanged={onChanged}
        onCreated={onCreated}
      />
    </div>
  );
}
